import React, { memo, useCallback } from 'react';
import { PopperProps, SvgIconProps, SxProps, Theme } from '@mui/material';

import { Dropdown, DropdownProps, DropdownOption } from '../Dropdown';
import { MiniClose24 } from '../../icons';
import {
    DropdownButton,
    DropdownButtonProps,
    useOpenState,
} from '../DropdownButton';


export type DropdownSelectProps<T extends DropdownProps['value'] = DropdownOption> = Pick<
DropdownButtonProps,
'disabled' | 'text' | 'size'
> &
Pick<DropdownProps, 'inputValue' | 'onInputChange'> & {
    selected: T;
    onSelect: (value: T) => void;
    options: DropdownOption[];
    onOpen?: () => void;
    onClose?: () => void;
    isMulti?: boolean;
    popperProps?: Pick<PopperProps, 'disablePortal' | 'sx'> & { dropdownSx?: SxProps<Theme> };
    isLoading?: boolean;
    buttonSx?: SxProps<Theme>;
};


export const DropdownSelect = memo<DropdownSelectProps>(
    ({
        selected, onSelect, options, onOpen, onClose, disabled, text, isMulti, size,
        popperProps: { dropdownSx, ...restPopperProps },
        isLoading, buttonSx,
    }) => {
        const openState = useOpenState(onOpen, onClose);

        const onChange = useCallback<DropdownSelectProps['onSelect']>(
            value => {
                onSelect(value);

                if (!isMulti)
                    openState.close();
            },
            [onSelect, openState, isMulti],
        );

        const onClear = useCallback<SvgIconProps['onClick']>(
            event => {
                event.stopPropagation();

                onChange(null);
            },
            [onChange],
        );

        return (
            <DropdownButton
                isLoading={isLoading}
                isOpen={openState.isOpen}
                isActive={!!selected}
                onClick={openState.change}
                close={openState.close}
                endIcon={
                    selected && (
                        <MiniClose24
                            sx={({ palette }) => ({
                                zIndex: 2,
                                color: palette.grayscale.shade100,
                            })}
                            onClick={onClear}
                        />
                    )
                }
                fullWidth
                disabled={disabled}
                text={text}
                size={size}
                sx={{
                    whiteSpace: ['nowrap'],
                    borderRadius: 3,
                    ...buttonSx,
                }}
                typographyProps={{
                    sx: {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    },
                }}
                popperProps={restPopperProps}
            >
                <Dropdown
                    value={isMulti && !selected ? [] : selected}
                    isMulti={isMulti}
                    // @ts-ignore
                    onChange={onChange}
                    options={options}
                    sx={{
                        maxHeight: ({ spacing }) => spacing(99),
                        width: '100%',
                        borderRadius: 3,
                        ...dropdownSx,
                    }}
                    itemTypographyProps={{
                        variant: 'regularCaption1',
                    }}
                />
            </DropdownButton>
        );
    },
);
